import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isTablet } from '@westwing/ui-kit/breakPoints';
import { OverlayId } from 'Client/components/Overlays/types';
import { hideOverlay, showOverlay } from 'Client/redux/overlays/actions';
import useShowPromoBarBanner from 'Client/hooks/flags/useShowPromoBarBanner';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import useUserTracking from 'Client/hooks/flags/useUserTracking';
import usePersistedFlag from 'Client/hooks/flags/usePersistedFlag';
import { PageType } from 'Client/hooks/UrlManager/pageTypeHooks';
import { ProductListingPages } from 'Client/hooks/UrlManager/types';
import useRouter from 'Client/hooks/useRouter';
import { getPageTypeByUrl } from 'Client/hooks/UrlManager/pageTypeService';
import useGa from 'Client/hooks/useGa';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';
import { smartAppUrl, Trackers } from 'Common/helpers/app';
import { TrackAppBannerActions } from 'Common/ga/types';
import StateInterface from 'Client/redux/types';
import { getOverlayById } from 'Client/redux/overlays/selectors';
import { useLaunchDarklyTracker } from 'Client/LaunchDarklyTracker';

export const SMART_BANNER_DISMISSED = 'smartBannerDismissed';

export const useShowSmartBanner = (id: string) => {
    const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
    const { p100AppBanner } = useFeatureToggle();
    const showClubHeader = useShowPromoBarBanner();
    const { isLoggedIn } = useUserTracking();
    const [dismissed] = usePersistedFlag(SMART_BANNER_DISMISSED, true);
    const {
        location: { pathname },
        host,
    } = useRouter();
    const { pageType: currentPageType } = getPageTypeByUrl(pathname, host);

    const recentlyDismissed = !!dismissed;

    const pageWhiteList = [...ProductListingPages, PageType.HOMEPAGE];

    const shouldShow = !!(
        pageWhiteList.includes(currentPageType) &&
        p100AppBanner === id && // For A/B/C test, to identify the correct Banner/Overlay
        !recentlyDismissed && // hide if shown in last 5 days
        !showClubHeader && // hide if shop2club banner is displayed
        isUserLoggedIn
    );

    useEffect(() => {
        // We need to do this to make sure the smartBanner loads only on client side.
        setIsUserLoggedIn(!!isLoggedIn);
    }, [isLoggedIn]);

    return shouldShow;
};

export const useActionsSmartBanner = (id: string, trackers: Trackers) => {
    const { p100AppBanner } = useFeatureToggle();
    const launchDarklyTracker = useLaunchDarklyTracker();
    const [, setDismissed] = usePersistedFlag(SMART_BANNER_DISMISSED, true);
    const {
        location: { pathname },
        host,
    } = useRouter();
    const bellaPush = useBellaPush();
    const gaTracking = useGa();
    const dispatch = useDispatch();
    const { pageType: currentPageType } = getPageTypeByUrl(pathname, host);

    const isDoiOverlayActive = useSelector<StateInterface, boolean>(
        state => getOverlayById(state.overlays, OverlayId.DOUBLE_OPT_IN_OVERLAY).isActive
    );

    const shouldShow = useShowSmartBanner(id);

    const expirationDays = 3;
    const cookieTTL = expirationDays * 60 * 60 * 1000 * 24;

    const onBackdropDismiss = () => {
        if (p100AppBanner === 'small') {
            dispatch(hideOverlay(OverlayId.APP_PUSH_SMALL));
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerCloseBackground, currentPageType);
        } else if (p100AppBanner === 'big') {
            dispatch(hideOverlay(OverlayId.APP_PUSH_BIG));
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.BigPushBannerCloseBackground, currentPageType);
        }
        setDismissed(1, cookieTTL);
    };

    const close = () => {
        if (launchDarklyTracker) {
            launchDarklyTracker.trackMetric('bella-app-banner-push-dismiss');
        }
        if (p100AppBanner === 'smart') {
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmartBannerClose, currentPageType);
        } else if (p100AppBanner === 'small') {
            dispatch(hideOverlay(OverlayId.APP_PUSH_SMALL));
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerClose, currentPageType);
        } else if (p100AppBanner === 'big') {
            dispatch(hideOverlay(OverlayId.APP_PUSH_BIG));
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.BigPushBannerClose, currentPageType);
        }
        setDismissed(1, cookieTTL);
    };

    const openInApp = () => {
        if (launchDarklyTracker) {
            // This would lead away from web to app so tracking request should be sent immediately.
            launchDarklyTracker.trackImportantMertic('bella-app-banner-push-click');
        }
        if (p100AppBanner === 'smart') {
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmartBannerClick, currentPageType);
        } else if (p100AppBanner === 'small') {
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerClick, currentPageType);
        } else if (p100AppBanner === 'big') {
            gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.BigPushBannerClick, currentPageType);
        }
        bellaPush(smartAppUrl(host, trackers));
    };

    useEffect(() => {
        if (!isTablet() && shouldShow) {
            if (launchDarklyTracker) {
                launchDarklyTracker.trackVariation('p100AppBanner', p100AppBanner);
            }
            if (p100AppBanner === 'smart') {
                gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmartBannerImpression, currentPageType);
            } else if (p100AppBanner === 'small' && !isDoiOverlayActive) {
                dispatch(showOverlay(OverlayId.APP_PUSH_SMALL));
                gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.SmallPushBannerImpression, currentPageType);
            } else if (p100AppBanner === 'big' && !isDoiOverlayActive) {
                dispatch(showOverlay(OverlayId.APP_PUSH_BIG));
                gaTracking.oneHeader.trackAppBanner(TrackAppBannerActions.BigPushBannerImpression, currentPageType);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShow]);

    return {
        close,
        openInApp,
        onBackdropDismiss,
    };
};
